import { useState } from "react";
import { Check, Copy, Link2, Share2 } from "lucide-react";

import { createFriendInvite } from "../../Services/FriendInviteService";

/**
 * "Invite friends": hämtar min personliga länk och delar den.
 *
 * Web Share där den finns — på telefonen är det den enda vägen till
 * WhatsApp, iMessage och resten utan att bygga en egen delningsmeny. På
 * desktop finns den sällan, och då kopieras länken i stället.
 *
 * Länken hämtas först när knappen trycks. En användare som aldrig bjuder
 * in någon ska inte ha en rad i databasen, och backend ger samma länk
 * tillbaka vid nästa tryck.
 *
 * navigator.share måste anropas i samma användargest som klicket. Anropet
 * till API:et hinner emellan, och Safari är strikt med det — därför
 * fallerar delningen tyst till kopiering nedan i stället för att visa ett
 * fel användaren inte kan göra något åt.
 */

const SHARE_TEXT = "Join me on Beatspot and see what's playing near you.";

/**
 * Web Share finns i praktiken bara på telefonen. Typerna säger att
 * navigator.share alltid finns, så kontrollen måste göras mot typeof —
 * annars optimerar TypeScript bort den och desktop får en knapp som inte
 * gör något.
 */
function canShare() {
  return typeof navigator.share === "function";
}

type Feedback = "shared" | "copied" | "failed" | null;

export default function InviteFriendsButton({ className = "" }: { className?: string }) {
  const [isBusy, setIsBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [url, setUrl] = useState<string | null>(null);

  const copy = async (link: string) => {
    // clipboard.writeText finns inte i osäkra kontexter (http på en
    // telefon i lokalnätet). Länken visas i så fall i klartext nedan, så
    // den går att markera för hand.
    if (!navigator.clipboard?.writeText) return false;

    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch {
      return false;
    }
  };

  const handleClick = async () => {
    setIsBusy(true);
    setFeedback(null);

    try {
      const { invite } = await createFriendInvite();
      setUrl(invite.url);

      if (canShare()) {
        try {
          await navigator.share({
            title: "Beatspot",
            text: SHARE_TEXT,
            url: invite.url,
          });

          setFeedback("shared");
          return;
        } catch (e) {
          // AbortError betyder att användaren stängde delningsarket. Det
          // är inte ett fel och ska inte ge ett kvitto på något.
          if ((e as Error)?.name === "AbortError") return;
        }
      }

      setFeedback((await copy(invite.url)) ? "copied" : "failed");
    } catch (e) {
      console.error("friend invite failed", e);
      setFeedback("failed");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="invite-friends">
      <button
        type="button"
        className={`follow-button ${className}`}
        onClick={handleClick}
        disabled={isBusy}
      >
        {feedback === "copied" || feedback === "shared" ? (
          <Check size={15} strokeWidth={2.2} />
        ) : canShare() ? (
          <Share2 size={15} strokeWidth={2.2} />
        ) : (
          <Link2 size={15} strokeWidth={2.2} />
        )}
        {isBusy ? "Getting link…" : "Invite friends"}
      </button>

      {feedback === "copied" && (
        <p className="invite-friends-note" role="status">
          Invite link copied.
        </p>
      )}

      {feedback === "shared" && (
        <p className="invite-friends-note" role="status">
          Invite link shared.
        </p>
      )}

      {/* Sista utvägen: varken delning eller urklipp fungerade. Länken
          visas då som text så att den går att kopiera för hand. */}
      {feedback === "failed" && (
        <p className="invite-friends-note invite-friends-note--error" role="status">
          {url ? (
            <>
              Copy this link: <span className="invite-friends-link">{url}</span>
            </>
          ) : (
            "Could not create an invite link. Try again."
          )}
        </p>
      )}

      {url && feedback !== "failed" && (
        <button
          type="button"
          className="invite-friends-copy"
          onClick={async () => setFeedback((await copy(url)) ? "copied" : "failed")}
        >
          <Copy size={13} strokeWidth={2.2} />
          Copy link
        </button>
      )}
    </div>
  );
}
